import { Inject, Injectable } from '@angular/core';
import {
  Storage,
  ref,
  UploadMetadata,
  uploadBytesResumable,
  UploadTask,
  deleteObject,
  getDownloadURL,
  getMetadata
} from '@angular/fire/storage';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FileStorageUploadProgress } from '../classes/file-storage';
import * as saveAs from 'downloadjs';
import { AppError, AppErrorTimeout } from '../classes/app-errors';
import { CacheFirebaseStorage } from 'src/app/features/cache/cache-urls';

export interface UploadedDocument {
  path: string;
  url: string;
  size: number;
  mime_type: string;
}

export interface UploadProgress {
  percentage: number;
  totalBytes: number;
  transferredBytes: number;
}

export interface UploadFileOptions {
  metadata?: UploadMetadata;
  maxAge?: number;
  path?: string;
}


export type StorageUploadCallback = (event: 'progress' | 'error' | 'done', data: UploadProgress | Error) => {};

export type OnProgressCallback = (percentage: number, total?: number, transferred?: number) => void;

export interface UploadProgressResult {
  progress: Observable<number>;
  downloadUrl: Observable<string>;
}


interface Cache {
  [index: string]: string;
}

@Injectable({ providedIn: 'root' })
export class StorageService {

  private _cache: Cache = {};
  private cache = new CacheFirebaseStorage();


  constructor(
    @Inject(Storage) private storage: Storage
  ) {
    // const cache = localStorage.getItem('storage_cache');
    // if (cache) {
    //   this._cache = JSON.parse(cache);
    // }

    // // save cache every minute
    // setInterval(() => {
    //   this.saveCache();
    // }, 60000);
  }

  async fileExists(path: string) {
    try {
      const sRef = ref(this.storage, path);
      const data = await getMetadata(sRef);
      return true;
    } catch (err) {
      return false;
    }
  }

  getFileAsBlob(path: string, onProgress?: OnProgressCallback): Promise<{ path: string, file: Blob }> {
    return new Promise(async (resolve, reject) => {
      let url = path;
      try {
        if (!url.startsWith('http')) {
          this.storage.maxOperationRetryTime = 30000;
          const sRef = ref(this.storage, path);
          url = await getDownloadURL(sRef);
        }

      } catch (err) {
        if (err.code_) {
          if (err.code_ == 'storage/retry-limit-exceeded') {
            reject(new AppErrorTimeout(`Não foi possível retornar a imagem.`));
            return;
          }
        }
        reject(new AppError('Falha ao baixar a imagem.'));
      }

      // This can be downloaded directly:
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onerror = (event) => {
        reject(event);
      };

      xhr.onprogress = event => {
        const progressAsPercent = Math.round(100 * event.loaded / event.total);
        if (onProgress) {
          onProgress(progressAsPercent, event.loaded, event.total);
        }
      }

      xhr.onload = (event) => {
        const blob = xhr.response;
        resolve({
          path, file: blob
        });
      };

      xhr.timeout = 10000;
      xhr.ontimeout = (ev) => {
        reject(new AppErrorTimeout('Tempo excedido esperando o download da imagem.'));
      };

      xhr.open('GET', url);
      xhr.send();
    });
  }

  getFileAsBase64(path: string): Promise<{ path: string, file: string }> {
    return new Promise(async (resolve, reject) => {

      try {
        const data = await this.getFileAsBlob(path);

        const reader = new FileReader();
        reader.readAsDataURL(data.file);
        reader.onloadend = () => {
          resolve({ path, file: reader.result as string });
        };

        reader.onerror = (error) => {
          reject(error);
        };
      } catch (err) {
        reject(err);
      }
    });

  }

  getFileFromComputer(accept: string): Promise<File> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.onchange = async () => {
        const file = input.files[0];
        resolve(file);
      }
      input.click();
    });

  }

  convertFileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  convertBase64ToFile(base64: string, fileName: string): Promise<File> {
    return new Promise((resolve, reject) => {
      const byteString = atob(base64.split(',')[1]);
      const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const file = new File([blob], fileName);
      resolve(file);
    });
  }

  async downloadFile(path: string, fileName: string, onProgress: OnProgressCallback): Promise<boolean> {
    const res = await this.getFileAsBlob(path, onProgress);
    const ret = saveAs(res.file, fileName, res.file.type);
    if (ret === true) {
      return true;
    } else if (ret === false) {
      return false;
    } else {
      return true;
    }
  }

  public updaloadFileV2(file: File, destination: string, options?: UploadFileOptions): { task: UploadTask, path: string } {
    let metadata: UploadMetadata = {};
    if (options) {
      if (options.metadata) {
        metadata = options.metadata;
      }

      if (options.maxAge) {
        metadata.cacheControl = `max-age=${options.maxAge}`;
      }
      if (Object.keys(metadata).length == 0) {
        metadata = undefined;
      }
    }


    const ext = file.name.split('.').pop();
    const fileName = `${new Date().getTime()}.${ext}`;
    destination += '/' + fileName;

    const sRef = ref(this.storage, destination);
    const task = uploadBytesResumable(sRef, file, metadata);

    return { task, path: destination };
  }

  public removeFile(path: string) {
    const _ref = ref(this.storage, path);
    return deleteObject(_ref);
  }

  private uploadFile(file: File, destination: string, options?: UploadFileOptions): Promise<string> {

    return new Promise<string>((resolve, reject) => {
      let metadata: UploadMetadata = {
        cacheControl: 'max-age=-1'
      };

      if (options) {
        if (options.metadata) {
          metadata = options.metadata;
        }

        if (options.maxAge) {
          metadata.cacheControl = `max-age=${options.maxAge}`;
        }
        if (Object.keys(metadata).length == 0) {
          metadata = undefined;
        }
      }

      const ext = file.name.split('.').pop();

      destination += '/' + this.generateUUID() + '.' + ext;

      const sRef = ref(this.storage, destination);
      uploadBytesResumable(sRef, file, metadata)
        .then(upload => {
          upload.task
            .then(_file => {
              resolve(_file.ref.fullPath);
            })
            .catch(err => {
              reject(err);
            });

        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public uploadWithProgress(file: File, destination: string, options?: UploadFileOptions): FileStorageUploadProgress {
    const metadata = this.adjustMetadata(options);
    const ext = file.name.split('.').pop();
    destination += '/' + this.generateUUID() + '.' + ext;

    return new FileStorageUploadProgress(this.storage, file, destination, metadata);

  }

  private adjustMetadata(options?: UploadFileOptions): UploadMetadata {
    let metadata: UploadMetadata = {
      cacheControl: 'max-age=2628000'
    };
    if (options) {
      if (options.metadata) {
        metadata = options.metadata;
      }

      if (options.maxAge) {
        metadata.cacheControl = `max-age=${options.maxAge}`;
      }
      if (Object.keys(metadata).length == 0) {
        metadata = undefined;
      }
    }

    return metadata;
  }

  getPublicURL(path: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      if (path.toLowerCase().startsWith('http')) {
        resolve(path);
      } else {
        const bucket = environment.firebase.config.storageBucket;

        const stored = await this.cache.get(path);

        if (stored) {
          resolve(stored);
          return;
        }
        const sRef = ref(this.storage, path);
        getDownloadURL(sRef)
          .then(async url => {
            // this._cache[path] = url;
            await this.cache.set(path, url, { expires: '30d' });
            resolve(url);

          })
          .catch(err => reject(err));
      }
    });
  }

  private generateUUID() {
    let d = new Date().getTime();
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      d += performance.now();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      // tslint:disable: no-bitwise
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      // tslint:enable: no-bitwise
    });
  }

  uploadImage(file: File, parentID: number) {
    return this.uploadFile(file, 'images/' + parentID.toString());
  }

  uploadWikiImage(file: File, parentID: number) {
    return this.uploadFile(file, 'wiki/' + parentID.toString());
  }

  uploadDocument(file: File, parentID: number) {
    return this.uploadFile(file, 'documents/' + parentID.toString());
  }

  uploadDocumentWithProgress(file: File, parentID: number) {
    return this.uploadWithProgress(file, 'documents/' + parentID.toString());
  }

  uploadImageProgress(file: File, parentID: number, options?: UploadFileOptions) {

    let path = 'images/' + parentID.toString();
    if (options?.path) {
      path = options.path;
    }

    return this.uploadWithProgress(file, path, options);
  }

  uploadProgress(file: File, parentID: number) {
    return this.uploadWithProgress(file, 'documents/' + parentID.toString());
  }

  async removeImage(path: string) {
    return true;
  }

}
